// ============================================================
// INVOICE STATS CARDS COMPONENT
// ============================================================

import { BadgeDollarSign, Clock, TrendingUp, Wallet } from 'lucide-react';
import { KPIWidget } from '../../../components/shared/KPIWidget';

interface InvoiceStats {
  total: number;
  paid: number;
  pending: number;
  cancelled: number;
  totalRevenue?: number;
  averageAmount?: number;
  pendingAmount?: number;
  currency?: string;
}

interface InvoiceStatsCardsProps {
  stats: InvoiceStats;
}

export function InvoiceStatsCards({ stats }: InvoiceStatsCardsProps) {
  const currency = stats.currency === 'XAF' ? 'FCFA' : stats.currency || 'FCFA';

  // Calculer le taux de paiement
  const paymentRate = stats.total > 0 ? Math.round((stats.paid / stats.total) * 100) : 0;

  // Formater les montants
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Chiffre d'affaires total */}
      {stats.totalRevenue !== undefined && (
        <KPIWidget
          title="Chiffre d'affaires"
          value={`${formatAmount(stats.totalRevenue)} ${currency}`}
          subtext="Factures payées"
          trend="up"
          color="primary"
          icon={<BadgeDollarSign />}
        />
      )}

      {/* Taux de paiement */}
      <KPIWidget
        title="Taux de paiement"
        value={`${paymentRate}%`}
        subtext={`${stats.paid}/${stats.total} factures`}
        trend={paymentRate >= 80 ? 'up' : 'down'}
        color={paymentRate >= 80 ? 'success' : 'warning'}
        icon={<TrendingUp />}
      />

      {/* Montant moyen */}
      {stats.averageAmount !== undefined && (
        <KPIWidget
          title="Montant moyen"
          value={`${formatAmount(stats.averageAmount)} ${currency}`}
          subtext="Par facture"
          trend="neutral"
          color="primary"
          icon={<Wallet />}
        />
      )}

      {/* Nombre total de factures générées */}
      <KPIWidget
        title="Factures générées"
        value={stats.total.toString()}
        subtext={`${stats.paid} payées • ${stats.pending} en attente`}
        trend="neutral"
        color="primary"
        icon={<Clock />}
      />
    </div>
  );
}
